import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import { languages as predefinedLanguages, Language } from "./../language-switch/LangInterface";
import { EonUiButton, EonUiDropdown, EonUiDropdownOption, EonUiInput } from "@eon-ui/eon-ui-components-react";
import "./RequestCallback.scss";
import { generateUUID } from "../feedback/sessionId";
import { getKID, getKIDEmail } from "../../util/auth";
import { EonUiDropdownCustomEvent, EonUiInputCustomEvent } from "@eon-ui/eon-ui-components";
import { EnvironmentConfig } from "@me8eon/config";
import { callServiceNowAPI, pollBackendAPI, delay } from "../../services/requestCallback";
import { getDecisionTree, getUserCommand } from "../ai-assist-refactored/quick-actions/decisionTree";
import LoaderComponent from "../loader-component/LoaderComponent";
import { useRequestCallback } from "./RequestCallbackContext";
import { Options } from "../ai-assist-refactored/quick-actions/quickActions.component";
import { replaceMultiple } from "@me8eon/string_utils";
import { getTimeInSeconds } from "../../util/date";
import { BooleanFeatureFlag, useFeatureFlag } from "@me8eon/featureflags";
import { aiConfig } from "me-at-eon/src";
import { useFeedback } from "@me8eon/feedback";
import { useUserData } from "@me8eon/react_authentication";
import { AiAssistContext } from "../ai-assist-refactored/AiAssist.context";

type PhoneManualEntryType = "" | "optional" | "mandatory";

const RequestCallback: React.FC<{ data: any }> = ({ data }) => {
    const [childKey, setChildKey] = useState(0);

    const handleReset = () => {
        setChildKey(prev => prev + 1);
    };

    return (
        <RequestCallbackChild key={childKey} data={data} handleReset={handleReset} />
    )
};

const RequestCallbackChild: React.FC<{ data: any, handleReset: () => void }> = ({ data, handleReset }) => {

    const { incidentText, isIncidentTextPending } = data;
    const { updateRequestCallback, getRequestCallback } = useRequestCallback();

    const [localSessionId, setLocalSessionId] = useState<string>("");
    const [channels, setChannels] = useState<any[]>([]);
    const [languages, setLanguages] = useState<any[]>([]);
    const [phones, setPhones] = useState<any[] | null>(null);
    const [channel, setChannel] = useState<string>("");
    const [language, setLanguage] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [phoneManualEntry, setPhoneManualEntry] = useState<PhoneManualEntryType>("");
    const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const [phoneEditable, setPhoneEditable] = useState<boolean>(true);
    const [confirmBtnClicked, setConfirmBtnClicked] = useState<boolean>(false);

    const { actualMessages } = useContext(AiAssistContext);
    const feedback = useFeedback();
    const { company, currentRole } = useUserData();

    const [agentLastMessageTimestamp, setAgentLastMessageTimestamp] = useState<string>(new Date(0).toISOString());
    const agentLastMessageTimestampRef = useRef(agentLastMessageTimestamp);

    const { t, i18n } = useTranslation();
    const lang = predefinedLanguages.find((lang: Language) => lang.code === i18n.language)?.name ?? "";
    const userKID = getKID();
    const userEmail = getKIDEmail();
    const isPhone = channel === 'Phone';
    const canConfirm = channel && language && (!isPhone || (phone && isValidPhone));
    const USER_MANUAL_ENTRY = "user_manual_entry";
    const MINIMUM_PHONES_FOR_DROPDOWN = 2;
    const { jwtTokenFn } = aiConfig;

    useEffect(() => {
        setLocalSessionId(generateUUID());
    }, []);

    useEffect(() => {
        agentLastMessageTimestampRef.current = agentLastMessageTimestamp;
    }, [agentLastMessageTimestamp]);

    useEffect(() => {
        if (!isIncidentTextPending && localSessionId) {
            updateRequestCallback(localSessionId, { status: "" });
            handleInitialRequests();
        }
    }, [localSessionId, isIncidentTextPending]);

    const getRequestCallbackPayload = (text: string, typed: boolean = true, action?: string) => {
        let payload: any = {
            requestId: localSessionId,
            botToBot: "true",
            clientSessionId: localSessionId,
            silentMessage: false,
            appInboundId: "me_eon",
            contextVariables: {
                requester_session_language: "en"
            },
            message: {
                text,
                typed
            },
            userId: userKID,
            emailId: userEmail
        };
        if (action) payload.action = action;
        return payload;
    };

    const handleInitialRequests = async () => {
        setIsLoading(true);
        try {
            const steps = [
                { name: 'end-conversation', payload: getRequestCallbackPayload("", true, "END_CONVERSATION") },
                { name: 'select-action-request-callback', payload: getRequestCallbackPayload("Request Callback", false) },
            ];
            const snowResponse = await callServiceNow(steps);
            if (!snowResponse.includes(false)) {
                const backendResponse = await callBackend(
                    (context: any) => context?.message?.text === "Request Callback",
                    (matched: any) => {
                        const options = matched?.body?.[0]?.options;
                        return options ? { options } : null;
                    }
                );
                setChannels(backendResponse?.options?.map((opt: any) => ({ value: opt.value, label: opt.label })) ?? []);
            }
        } catch {
            triggerRequestCallbackError();
        } finally {
            setIsLoading(false);
        }
    };

    const handleChannelSelect = async (selected: string) => {
        setIsLoading(true);
        setChannel(selected);
        setLanguage("");
        setLanguages([]);
        updateRequestCallback(localSessionId, { channel: selected, language: "" });
        try {
            const steps = [
                { name: 'select-channel', payload: getRequestCallbackPayload(selected, false) },
            ];
            const snowResponse = await callServiceNow(steps);
            if (!snowResponse.includes(false)) {
                const backendResponse = await callBackend(
                    (context: any) => context?.message?.text === selected,
                    (matched: any) => {
                        const options = matched?.body?.[0]?.options;
                        return options ? { options } : null;
                    }
                );
                const options = backendResponse?.options?.map((opt: any) => ({ value: opt.value, label: opt.label })) ?? [];
                setLanguages(options);
            }
        } catch {
            triggerRequestCallbackError();
        } finally {
            setIsLoading(false);
        }
    };

    const handleLanguageSelect = async (selected: string) => {
        setIsLoading(true);
        setLanguage(selected);
        updateRequestCallback(localSessionId, { language: selected });
        try {
            const steps = [
                { name: 'select-language', payload: getRequestCallbackPayload(selected, false) },
            ];
            const snowResponse = await callServiceNow(steps);
            if (!snowResponse.includes(false)) {
                const backendResponse = await callBackend(
                    (context: any) => context?.message?.text === selected,
                    (matched: any) => {
                        const body = matched?.body?.[0];
                        return body ? { body } : null;
                    }
                );
                const options: any[] = formatPhoneNumbers(backendResponse?.body?.options);
                if (options.length === 0) {
                    setPhoneManualEntry("mandatory");
                    setIsValidPhone(false);
                } else if (options.length == MINIMUM_PHONES_FOR_DROPDOWN) {
                    const phone = options[0].label;
                    setPhone(phone);
                    updateRequestCallback(localSessionId, { phone });
                    setPhoneManualEntry("optional");
                    setPhoneEditable(false);
                    setIsValidPhone(isValidInternationalPhoneNumber(phone));
                }
                setPhones(options);
            }
        } catch {
            triggerRequestCallbackError();
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhoneSelect = async (selected: string) => {
        if (selected === USER_MANUAL_ENTRY) {
            setPhone("");
            updateRequestCallback(localSessionId, { phone: "" });
            setPhoneManualEntry("optional");
            setIsValidPhone(false);
        } else {
            setPhone(selected);
            updateRequestCallback(localSessionId, { phone: selected });
            setPhoneManualEntry("");
        }
    };

    const handleConfirm = async () => {
        setConfirmBtnClicked(true);
        const finalIncidentText = data.incidentText ?? incidentText;
        const str = getUserCommand("confirmRequestCallback");
        const contextValues = getRequestCallback(localSessionId);
        const replacements: Record<string, string> = {
            "<channel>": contextValues?.channel ?? "",
            "<phone>": isPhone ? (contextValues?.phone ? `(${contextValues.phone})` : "") : "",
            "<language>": contextValues?.language ?? "",
            "  ": " "
        };
        if (data.callback) data.callback({ nextNodeId: "confirmRequestCallback", data: { requestCallbackSessionId: localSessionId } });
        feedback({
            source: "request-callback",
            messages: actualMessages,
            short_description: "MyHelp - " + finalIncidentText,
            query: "MyHelp - Request Callback",
            company: company || "unknown",
            role: currentRole || "unknown",
        });
        try {
            let steps: any = [];
            if (isPhone) {
                if (phoneManualEntry === "optional") {
                    steps = [...steps, ...[{ name: 'phone-manual-entry', payload: getRequestCallbackPayload("user_manual_entry", false) }]];
                }
                steps = [...steps, ...[{ name: 'input-phone', payload: getRequestCallbackPayload(phone) }]];
                if (phoneManualEntry !== "") {
                    steps = [...steps, ...[{ name: 'confirm-phone', payload: getRequestCallbackPayload("Yes") }]];
                }
            }
            steps = [...steps, ...[{ name: 'describe-issue', payload: getRequestCallbackPayload(finalIncidentText) }]];
            const snowResponse = await callServiceNow(steps);
            if (!snowResponse.includes(false)) {
                const backendResponse = await callBackend(
                    (context: any) => context?.message?.text === finalIncidentText,
                    (matched: any) => {
                        const outputText = matched?.body?.[0]?.value?.startsWith("All set");
                        return outputText ? { outputText } : null;
                    }
                );
                updateRequestCallback(localSessionId, { status: backendResponse?.outputText ? "success" : "failed" });
            }
        } catch {
            triggerRequestCallbackError(true);
        }
    };

    const handleCancel = () => {
        const str = t("chatBot.quickActions.actions.cancelRequestCallback");
        if (data.callback) data.callback({ nextNodeId: "cancelRequestCallback", data: { requestCallbackSessionId: localSessionId } });
        setConfirmBtnClicked(true);
    }

    const callServiceNow = async (steps: any) => {
        const finalResponse = [];
        for (const step of steps) {
            try {
                const headers = {
                    "Content-Type": "application/json",
                    "x-proxy": EnvironmentConfig.snowRequestCallbackProxy,
                    "x-proxy-token": await jwtTokenFn()
                };
                const response = await callServiceNowAPI(step.payload, headers);
                finalResponse.push(response);
            } catch (e) {
                throw new Error('SNOW API Failed');
            }
            await delay(1000);
        }
        return finalResponse;
    }

    const callBackend = async (
        condition: (context: Record<string, any>) => boolean,
        extractResult: (matched: any) => any
    ) => {
        try {
            const headers = {
                "Content-Type": "application/json",
                "x-proxy": EnvironmentConfig.beRequestCallbackProxy,
                "x-proxy-token": await jwtTokenFn()
            };
            const payload = {
                sessionId: localSessionId,
                timestamp: agentLastMessageTimestampRef.current
            };
            const response = await pollBackendAPI(
                payload,
                headers,
                condition,
                extractResult,
                10,
                1000
            );
            if (new Date(response.updatedAt).getTime() > new Date(agentLastMessageTimestampRef.current).getTime()) {
                setAgentLastMessageTimestamp(response.updatedAt);
            }
            return response;
        } catch (e) {
            throw new Error('Backend API failed');
        }
    };

    const isValidInternationalPhoneNumber = (phone: string): boolean => {
        const regex = /^\+\d{1,3}\d{10}$/;
        return regex.test(phone);
    };

    const triggerRequestCallbackError = (confirmBtnClicked: boolean = false) => {
        if (confirmBtnClicked) {
            updateRequestCallback(localSessionId, { status: "failed" });
        } else {
            setError(true);
        }
        setConfirmBtnClicked(true);
    };

    const formatPhoneNumbers = (options: any) => {
        if (!options?.length) return [];
        const newOptions = Array.from(
            new Map(
                options.map((opt: any) => {
                    const value = opt.value === USER_MANUAL_ENTRY ? opt.value : opt.label;
                    const label = opt.value === USER_MANUAL_ENTRY ? t("chatBot.requestCallback.newNumber") : opt.value;
                    return [label, { value, label }];
                })
            ).values()
        );
        return newOptions;
    };

    return (
        <div className="request-callback-container">
            {isLoading && <LoaderComponent isLoading />}
            <div className="user-input">
                <div className="channel-language-box">
                    <div className="channel">
                        <div className="text">{t("chatBot.requestCallback.channelsText")}</div>
                        <div className="dropdown">
                            <EonUiDropdown
                                required={true}
                                onValueChanged={(e: EonUiDropdownCustomEvent<string>) => handleChannelSelect(e.target.value)}
                                onDropdownOpened={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.zIndex = getTimeInSeconds().toString(); }}
                                onDropdownClosed={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.removeProperty('z-index'); }}
                                name="channel"
                                size="small"
                                label={t("chatBot.requestCallback.channelLabel")}
                                showSelectedItems={true}
                                disabled={confirmBtnClicked || !channels.length || !!channel}
                            >
                                {channels.map((channel, index) => (
                                    <EonUiDropdownOption key={index} value={channel.value} label={channel.label} />
                                ))}
                            </EonUiDropdown>
                        </div>
                    </div>
                    <div className="language">
                        <div className="text">{t("chatBot.requestCallback.languageText")}</div>
                        <div className="dropdown">
                            <EonUiDropdown
                                required={true}
                                onValueChanged={(e: EonUiDropdownCustomEvent<string>) => handleLanguageSelect(e.target.value)}
                                onDropdownOpened={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.zIndex = getTimeInSeconds().toString(); }}
                                onDropdownClosed={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.removeProperty('z-index'); }}
                                name="language"
                                size="small"
                                label={t("chatBot.requestCallback.languageLabel")}
                                showSelectedItems={true}
                                disabled={confirmBtnClicked || !languages.length || !!language}
                                height="200px"
                            >
                                {languages.map((language, index) => (
                                    <EonUiDropdownOption key={index} value={language.value} label={language.label} />
                                ))}
                            </EonUiDropdown>
                        </div>
                    </div>
                </div>

                {isPhone && phones && <div className="phone-number-box">
                    {phones.length > MINIMUM_PHONES_FOR_DROPDOWN && <div className="available-numbers">
                        <div className="text">{t("chatBot.requestCallback.availableMobileNumbers")}</div>
                        <div className="dropdown">
                            <EonUiDropdown
                                required={true}
                                onValueChanged={(e: EonUiDropdownCustomEvent<string>) => handlePhoneSelect(e.target.value)}
                                onDropdownOpened={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.zIndex = getTimeInSeconds().toString(); }}
                                onDropdownClosed={(e: EonUiDropdownCustomEvent<string>) => { e.target.style.removeProperty('z-index'); }}
                                name="phone"
                                size="small"
                                label={t("chatBot.requestCallback.phoneLabel")}
                                showSelectedItems={true}
                                disabled={confirmBtnClicked || !phones.length}
                            >
                                {phones.map((phone, index) => (
                                    <EonUiDropdownOption key={index} value={phone.value} label={phone.label} />
                                ))}
                            </EonUiDropdown>
                        </div>
                    </div>}
                    {phoneManualEntry !== "" && <div className="chosen-number">
                        <div className="text">{t("chatBot.requestCallback.chosenNumber")}</div>
                        <div className="input">
                            <EonUiInput
                                type="text"
                                size="small"
                                className="phone-number"
                                label={t("chatBot.requestCallback.phoneLabel")}
                                labelVisuallyHidden
                                value={phone}
                                trailingIconName="edit_no_line"
                                placeholder="+491123456789"
                                readOnly={!confirmBtnClicked && !phoneEditable}
                                disabled={confirmBtnClicked}
                                required={true}
                                errorMessage={t("chatBot.requestCallback.phoneError")}
                                invalid={!isValidPhone}
                                onIconClick={(e) => {
                                    setPhoneEditable(true);
                                    setTimeout(() => {
                                        e.target.focusInput();
                                    }, 500);
                                }}
                                onValueChanged={(e: EonUiInputCustomEvent<string>) => {
                                    setPhoneManualEntry(phones.length >= MINIMUM_PHONES_FOR_DROPDOWN ? "optional" : "mandatory");
                                    setPhone(e.target.value);
                                    updateRequestCallback(localSessionId, { phone: e.target.value });
                                    setIsValidPhone(isValidInternationalPhoneNumber(e.target.value));
                                }}
                            />
                        </div>
                    </div>}

                </div>}
            </div>
            {error &&
                <>
                    <div className="error">{t("chatBot.requestCallback.technicalIssue")}</div>
                    <Options data={getDecisionTree().find(item => item.id === "confirmRequestCallback")} isButton={true}/>
                </>
            }
            {!error &&
                <div className="btn-group">
                    <div className="confirm-reset-btn">
                        <EonUiButton
                            key="confirm"
                            title={t("chatBot.requestCallback.tooltip.confirmBtn")}
                            rank="primary"
                            text={t("confirm")}
                            className="citation-item"
                            size="small"
                            onClick={() => {
                                if (confirmBtnClicked || !canConfirm || isLoading) return;
                                handleConfirm();
                            }}
                            disabled={confirmBtnClicked || !canConfirm || isLoading}
                        ></EonUiButton>
                        <EonUiButton
                            key="reset"
                            title={t("chatBot.requestCallback.tooltip.resetBtn")}
                            rank="secondary"
                            text={t("reset")}
                            className="citation-item"
                            size="small"
                            onClick={() => {
                                if (confirmBtnClicked || isLoading || !channel) return;
                                handleReset();
                            }}
                            disabled={confirmBtnClicked || isLoading || !channel}
                        ></EonUiButton>
                    </div>
                    <div className="cancel-btn">
                        <EonUiButton
                            key="cancel"
                            title={t("chatBot.requestCallback.tooltip.cancelBtn")}
                            rank="primary"
                            scheme="discreet"
                            text={t("cancel")}
                            size="small"
                            className="citation-item"
                            onClick={() => {
                                if (confirmBtnClicked || isLoading) return;
                                handleCancel();
                            }}
                            disabled={confirmBtnClicked || isLoading}
                        ></EonUiButton>
                    </div>
                </div>
            }
        </div>
    );
};

export default RequestCallback;
